// Meldungstext für die Sammelaktionen im Lister.
//
// Vorher meldete der Lister bei Teilfehlern nur „Mindestens ein Anlegen / Aktualisieren ist
// fehlgeschlagen - siehe Worker-Logs". Ein Anwender kommt an die Worker-Logs nicht heran, und
// welcher Artikel betroffen war, stand nirgends (Testablauf 13.3, 21.08.2026). Zwei Lücken:
//   1. Der Grund fehlte, obwohl er im Job steht (`job.error` bzw. seit heute `result.failures`).
//   2. Ein Job mit `status='completed'` und `result.failed > 0` galt als voller Erfolg — ein
//      Teil-Fehlschlag wurde also als „erledigt" gemeldet.
// Beides deckt `summarizeBulkOutcome` ab. Reine Funktion, damit sie testbar bleibt.

/** Fehlgeschlagener Einzel-Artikel, wie der Worker ihn im Job-Result mitschickt. */
export interface JobItemFailure {
  articleId?: string;
  message?: string;
}

/** Das, was der Aufrufer nach `awaitJob` je Job in der Hand hat. */
export interface BulkJobOutcome {
  status: string;
  error?: string | null;
  result?: unknown;
  /**
   * Sprechender Name des Schritts, z. B. „Bestand" oder „Bilder". Nur nötig, wenn der Job KEINE
   * Einzelfehler mitführt (die Push-Jobs zählen bloß) — dann steht im Text wenigstens, WELCHER
   * Schritt wie viele Artikel verloren hat, statt einer nackten Zahl.
   */
  label?: string;
}

export interface BulkSummary {
  /** true = irgendwas ist schiefgegangen (Job hart gescheitert ODER Einzel-Artikel gescheitert). */
  hasFailures: boolean;
  /** Anzahl betroffener Artikel bzw. hart gescheiterter Jobs. */
  failedCount: number;
  /** Einzeiler für Toast/Statusleiste (gekürzt, maximal drei genannte Einträge). */
  message: string;
  /**
   * ALLE Fehler einzeln, direkt als `items` für `openErrorModal` verwendbar: `label` ist die
   * Artikelnummer bzw. der Schritt, `message` die Ursache. Ein Einzeiler mitten im Lister war
   * unübersichtlich (User-Befund 2026-08-21) — im Modal steht jeder Fehler auf seiner Zeile,
   * ungekürzt und mit „Als JSON kopieren".
   */
  items: Array<{ label?: string; message: string }>;
}

/** Länge, ab der eine einzelne Ursache gekürzt wird — Toasts sollen lesbar bleiben. */
const MAX_REASON_LEN = 160;
/** So viele Artikel werden namentlich genannt, der Rest als „und N weitere". */
const MAX_NAMED = 3;

function shorten(text: string): string {
  const t = text.trim().replace(/\s+/g, ' ');
  return t.length > MAX_REASON_LEN ? `${t.slice(0, MAX_REASON_LEN - 1)}…` : t;
}

function readFailures(result: unknown): { items: JobItemFailure[]; truncated: number; failed: number } {
  if (!result || typeof result !== 'object') return { items: [], truncated: 0, failed: 0 };
  const r = result as Record<string, unknown>;
  const items = Array.isArray(r['failures']) ? (r['failures'] as JobItemFailure[]) : [];
  const truncated = typeof r['failuresTruncated'] === 'number' ? (r['failuresTruncated'] as number) : 0;
  const failed = typeof r['failed'] === 'number' ? (r['failed'] as number) : 0;
  return { items, truncated, failed };
}

/**
 * Baut den Meldungstext für eine Sammelaktion.
 *
 * `articleLabel` löst die Vario-Artikel-ID auf die Artikelnummer auf — die kennt der Lister aus
 * seinem eigenen Grid, der Worker müsste sie sonst extra nachladen.
 * `successMessage` / `actionLabel` machen die Funktion für beide Sammelaktionen nutzbar.
 */
export function summarizeBulkOutcome(
  jobs: ReadonlyArray<BulkJobOutcome>,
  opts: {
    actionLabel: string;
    successMessage: string;
    articleLabel?: (articleId: string) => string | null;
    /** Womit gezählt wird, wenn ein Job keine Einzelfehler mitführt. Default „Artikel". */
    itemNoun?: string;
  },
): BulkSummary {
  const hardFailed = jobs.filter((j) => j.status !== 'completed');
  const named: string[] = [];
  const items: Array<{ label?: string; message: string }> = [];
  let itemFailures = 0;
  let truncatedTotal = 0;

  for (const job of jobs) {
    if (job.status !== 'completed') continue;
    const { items: failureItems, truncated, failed } = readFailures(job.result);
    itemFailures += failed;
    truncatedTotal += truncated;
    for (const f of failureItems) {
      const label = (f.articleId ? opts.articleLabel?.(f.articleId) : null) ?? f.articleId ?? 'Artikel';
      named.push(f.message ? `${label} (${shorten(f.message)})` : label);
      items.push({ label, message: f.message ?? 'Unbekannter Fehler' });
    }
    // Job zählt nur (`failed`), führt aber keine Einzelfehler mit (so arbeiten die Push-Jobs) →
    // wenigstens den Schritt benennen. Das ist eine BESCHREIBUNG der schon gezählten Fehler,
    // kein zusätzlicher Fehler.
    if (failureItems.length === 0 && truncated === 0 && failed > 0) {
      const noun = opts.itemNoun ?? 'Artikel';
      named.push(job.label ? `${job.label}: ${failed} ${noun}` : `${failed} ${noun}`);
      items.push({
        ...(job.label !== undefined ? { label: job.label } : {}),
        message: `${failed} ${noun} nicht übertragen. Einzelheiten stehen im Protokoll.`,
      });
    }
    if (truncated > 0) {
      items.push({ message: `Weitere ${truncated} Fehler ohne Detailtext, Einzelheiten stehen im Protokoll.` });
    }
  }

  // Hart gescheiterte Jobs: der Grund steht in job.error (ein Job kann mehrere Artikel umfassen,
  // deshalb wird hier der Job gezählt, nicht der Artikel).
  for (const job of hardFailed) {
    const reason = job.error ? shorten(job.error) : `Job endete mit Status ${job.status}`;
    named.push(`Durchlauf abgebrochen (${reason})`);
    items.push({
      label: job.label ? `Durchlauf abgebrochen: ${job.label}` : 'Durchlauf abgebrochen',
      message: job.error ?? `Job endete mit Status ${job.status}`,
    });
  }

  // `failed` des Jobs zaehlt ALLE gescheiterten Artikel, auch die ohne Detailtext — truncatedTotal
  // darf hier also NICHT dazuaddiert werden (sonst Doppelzaehlung).
  const failedCount = itemFailures + hardFailed.length;
  if (failedCount === 0) {
    return { hasFailures: false, failedCount: 0, message: opts.successMessage, items: [] };
  }

  const shown = named.slice(0, MAX_NAMED);
  // Nicht genannte: die uebrigen Detail-Eintraege PLUS die, fuer die der Worker keinen Text mehr mitgab.
  const rest = (named.length - shown.length) + truncatedTotal;
  const head = `${opts.actionLabel}: ${failedCount === 1 ? '1 Fehler' : `${failedCount} Fehler`}`;
  const more = rest > 0 ? `, und ${rest} weitere` : '';
  const detail = shown.length > 0 ? ` (${shown.join('; ')}${more})` : '';
  return {
    hasFailures: true,
    failedCount,
    message: `${head}${detail}. Die Ursache steht auch am jeweiligen Artikel unter „Letzter Fehler".`,
    items,
  };
}

/**
 * Sprechender Schritt-Name je Worker-Job-Typ, für `BulkJobOutcome.label`. Die Push-Jobs führen
 * keine Einzelfehler mit, deshalb ist der Schritt-Name die einzige Chance, dem Anwender zu sagen,
 * WAS nicht durchkam. Unbekannter Typ → null (dann steht nur die Anzahl im Text).
 */
export function jobStepLabel(type: string | null | undefined): string | undefined {
  switch (type) {
    case 'gambio.sync_listing_to_gambio': return 'Artikeldaten';
    case 'gambio.push_stock': return 'Bestand';
    case 'gambio.push_price': return 'Preis';
    case 'gambio.push_special_price': return 'Sonderpreis';
    case 'gambio.push_images': return 'Bilder';
    case 'gambio.create_variants_from_vario': return 'Varianten';
    case 'gambio.bulk_assign_vario_articles': return 'Verknüpfen';
    case 'gambio.bulk_seed_vario_articles': return 'Anlegen in VARIO';
    case 'gambio.sync_listing_to_vario': return 'Aktualisierung aus Gambio';
    case 'gambio.refresh_items': return 'Produktabgleich';
    case 'gambio.refresh_vario_listings': return 'Vario-Artikel-Ermittlung';
    case 'gambio.refresh_match_suggestions': return 'Trefferermittlung';
    case 'gambio.unassign_vario_listing': return 'Aus dem Gambio-Kanal nehmen';
    case 'vario.refresh_article_stocks': return 'Bestandsabgleich';
    default: return undefined;
  }
}
